import * as moment from 'moment';
import { GioStorage } from 'src/app/core/classes/gio-storage';
import { Indexable } from 'src/app/core/models/indexable';

export type CacheGroup = 'default' | 'categories' | 'competitions' | 'processes' | 'championships' | 'entity' | 'user' | 'internal'
  | 'fanclubresults' | 'relations' | 'merchandises' | 'purshases' | 'solarium';


export const CACHE_GROUP: Indexable<CacheGroup, string> = {
  default: 'Sistema',
  categories: 'Categorias',
  competitions: 'Competições',
  processes: 'Processos',
  championships: 'Campeonatos',
  entity: 'Entidades',
  user: 'Usuários',
  internal: 'Interno',
  fanclubresults: 'Resultados de Fanclub',
  relations: 'Relações',  
  merchandises: 'Merchandises',
  purshases: 'Compras',
  solarium: 'Solarium mail Service'
}
export interface CacheOptions {
  group?: CacheGroup;
  expires?: string;
}

export class CacheItem<T = any> {
  public createdAt: string;
  private validUntil: string;
  public group: CacheGroup;

  constructor(public id: string, public payload: T, options: CacheOptions) {
    this.createdAt = moment().toISOString();
    this.validUntil = this.getValidity(options?.expires ?? '1m').toISOString();
    this.group = options?.group ?? 'default';
  }

  static fromStore(item: CacheItem) {
    const cached = new CacheItem(item.id, item.payload, {
      expires: '1s',
      group: item.group,

    });

    cached.validUntil = item.validUntil;
    cached.createdAt = item.createdAt;
    return cached;
  }

  isValid(accepted?: string) {
    const validUntil = accepted ? this.getValidity(accepted) : moment(this.validUntil);
    return moment().isBefore(validUntil);
  }

  getValidity(expires: string): moment.Moment {
    if (!expires) {
      return moment(this.validUntil);
    }

    const _expires = expires;
    const int = parseInt(_expires);
    const t = _expires.replace(int.toString(), '').toLowerCase();
    let ms = int;
    if (t == 'ms') {
      ms = int;
    } else if (t == 's') {
      ms = int * 1000;
    } else if (t == 'm') {
      ms = int * (1000 * 60);
    } else if (t == 'h') {
      ms = int * (1000 * 60 * 60);
    } else if (t == 'd') {
      ms = int * (1000 * 60 * 60 * 24);
    } else {
      throw new Error('Cache expires conversions not accepted');
    }

    const v = moment(this.createdAt).add(ms, 'milliseconds');
    return v;
  }


}

export interface StorageTable {
  id: string;
  createdAt: string;
  group: StorageGroup;
}
export type StorageGroup = 'core' | 'performance' | 'analytics' | 'suppliers' | 'internal';
export class StorageTablePattern<T = any>{
  protected _storage: GioStorage;
  constructor(protected _tableName: string, protected group: StorageGroup) {
    this._storage = GioStorage.getInstance();
    this.registerTable().then();
  }

  protected get storage() {
    return this._storage;
  }

  private async registerTable() {

    const inDb = await this._storage.collection('tables').doc(this._tableName).get();
    try {
      const createdAt = moment().toISOString();
      const data: any = {
        id: this._tableName, group: this.group
      };
      if (!inDb) {
        data.createdAt = createdAt;
      }
      await this._storage.collection('tables').doc(this._tableName).set({ id: this._tableName, createdAt, group: this.group });
    } catch (error) {
      console.error('Erro ao criar a tabela de cache: ' + this._tableName);
    }

  }

  protected deleteTable() {
    return this._storage.collection(this._tableName).delete();
  }

  protected collection<Y = T>() {
    return this._storage.collection<Y>(this._tableName);
  }

  protected doc<Y = T>(key: string) {
    return this._storage.collection<Y>(this._tableName).doc(key);
  }

}

export class StorageTable<T = any> extends StorageTablePattern<T>{

  constructor(_tableName: string, group: StorageGroup) {
    super(_tableName, group);
  }

  protected async _get<Y = T>(key: string) {
    const res = await this._storage.collection<Y>(this._tableName).doc(key).get();
    return res;
  }

  protected async _set<Y = T>(key: string, value: object) {
    const setted = await this._storage.collection<Y>(this._tableName).doc(key).set(value);
    return setted;
  }

  list<Y = T>() {
    return this.collection<Y>().get();
  }

  listTables() {
    return this._storage.listTables();
  }
}

export class CacheTable<T = any> extends StorageTablePattern<T> {

  constructor(_tableName: string, group: StorageGroup) {
    super(_tableName, group);
  }


  async get<Y = T>(key: string, accepts?: string): Promise<Y> {
    const cacheApiItem = await this.doc<CacheItem<Y>>(key).get();

    if (!cacheApiItem) {
      return null;
    }
    const item = CacheItem.fromStore(cacheApiItem);
    if (!item.isValid(accepts)) {
      await this.delete(key);
      return null;
    }

    return cacheApiItem.payload;
  }

  async getLastCache<Y = T>(key: string): Promise<CacheItem<Y>> {
    const cacheApiItem = await this.doc<CacheItem<Y>>(key).get();

    if (!cacheApiItem) {
      return null;
    }
    const item = CacheItem.fromStore(cacheApiItem);
    return item;
  }



  set<Y = T>(key: string, value: Y, params: CacheOptions) {
    if (!params.expires) {
      params.expires = '1m';
    }
    const cacheApiItem = new CacheItem(key, value, params);
    return this.doc<CacheItem<Y>>(key).set(cacheApiItem);
  }

  delete(key: string) {
    return this.doc(key).delete();
  }

  async invalidateGroups(groups: CacheGroup | CacheGroup[]) {
    return this.cleanStore(Array.isArray(groups) ? groups : [groups]);
  }

  async invalidateAll() {
    return this.cleanStore(null, true);
  }


  async cleanStore(groups?: CacheGroup[], all: boolean = false) {

    let items = await this.collection<CacheItem>().get();
    while (items == null) {
      await Promise.delayed(500);
      items = await this.collection<CacheItem>().get();
    }

    if (items) {
      const _ps: Promise<void>[] = [];

      if (!Array.isArray(items)) {
        return;
      }
      items.forEach(item => {
        const chached = CacheItem.fromStore(item);

        if (all) {
          _ps.push(this.delete(chached.id));
          return;
        }

        if (groups && groups.length > 0) {
          if (groups.notIncludes(chached.group)) {
            return;
          }
          _ps.push(this.delete(chached.id));
          return;
        }

        if (!chached.isValid() && chached.id) {
          _ps.push(this.delete(chached.id));
        }
      });

      await Promise.all(_ps);
    }
  }

  async list() {
    return this.storage.collection<CacheItem<T>>(this._tableName).get()
  }





}
